import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Pencil, Plus } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTO_LOADING_MODEL } from "../../contexts/ModelContext";
import { useMode } from "../../contexts/ModeContext";
import { useWindow } from "../../contexts/WindowContext";
import { Mode } from "../../types/mode";
import { Model } from "../../types/model";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import "../../Styles/Layout/ResponseSettingsPicker.css";

interface ResponseSettingsPickerProps {
  currentMode: Mode;
  setMode: (mode: Mode) => void;
  currentModel: Model;
  setCurrentModel: (model: Model) => void;
  isChat?: boolean;
}

export const ResponseSettingsPicker: React.FC<ResponseSettingsPickerProps> = ({
  currentMode,
  setMode,
  currentModel,
  setCurrentModel,
  isChat = false,
}) => {
  const [open, setOpen] = useState(false);
  const { user, modelRegistry, initialPayloadLoaded } = useAuth();
  const { windowWidth } = useWindow();
  const {
    availableModes,
    setIsModeModalOpen,
    handleModeEdit,
    startCreateMode,
  } = useMode();

  const models = useMemo(() => Object.values(modelRegistry ?? {}), [modelRegistry]);
  // Keep the normal submenu gap when space allows. On narrow screens, a
  // negative offset progressively overlaps the parent menu instead of letting
  // the submenu extend beyond the viewport.
  const submenuSideOffset = Math.max(-190, Math.min(6, windowWidth - 447));
  const modelLabel = user && !initialPayloadLoaded
    ? AUTO_LOADING_MODEL.displayName
    : currentModel.displayName || AUTO_LOADING_MODEL.displayName;

  const createMode = () => {
    startCreateMode();
    setOpen(false);
    setIsModeModalOpen(true);
  };

  const editMode = (event: React.MouseEvent, mode: Mode) => {
    event.preventDefault();
    event.stopPropagation();
    handleModeEdit(mode);
    setOpen(false);
    setIsModeModalOpen(true);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`response-settings-trigger ${open ? "active" : ""}`}
          title="Mode and model settings"
          aria-label={`Mode and model settings. Model: ${modelLabel}. Mode: ${currentMode.name}`}
        >
          <span className="response-settings-model-name">{modelLabel}</span>
          <span className="response-settings-separator" aria-hidden="true">·</span>
          <span className="response-settings-mode-name">{currentMode.name}</span>
          <ChevronDown className="response-settings-chevron" size={13} strokeWidth={2} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="response-settings-menu"
        side={isChat ? "top" : "bottom"}
        align="end"
        sideOffset={8}
      >
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="response-settings-menu-item">
            <span className="response-settings-menu-label">Mode</span>
            <span className="response-settings-menu-value">{currentMode.name}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              className="response-settings-submenu"
              sideOffset={submenuSideOffset}
              collisionPadding={8}
            >
              {availableModes.map((mode) => {
                const selected = mode.id === currentMode.id;
                return (
                  <DropdownMenuItem
                    key={mode.id}
                    className={`response-settings-option mode-option ${selected ? "selected" : ""}`}
                    onSelect={() => setMode(mode)}
                  >
                    <span className="response-settings-option-label">{mode.name}</span>
                    <span className="response-settings-option-actions">
                      {selected && <Check className="response-settings-check" size={14} aria-hidden="true" />}
                      <button
                        type="button"
                        className="response-settings-edit-button"
                        disabled={!user}
                        title={user ? `Edit ${mode.name} mode` : "Sign in to edit modes"}
                        aria-label={user ? `Edit ${mode.name} mode` : "Sign in to edit modes"}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => editMode(event, mode)}
                      >
                        <Pencil size={12} aria-hidden="true" />
                      </button>
                    </span>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="response-settings-option response-settings-create-option"
                disabled={!user}
                title={user ? "Create a new mode" : "Sign in to create modes"}
                onSelect={createMode}
              >
                <Plus size={14} aria-hidden="true" />
                <span>New mode</span>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="response-settings-menu-item">
            <span className="response-settings-menu-label">Model</span>
            <span className="response-settings-menu-value">{modelLabel}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              className="response-settings-submenu"
              sideOffset={submenuSideOffset}
              collisionPadding={8}
            >
              {!user ? (
                <div className="response-settings-empty">Sign in to choose a model.</div>
              ) : !initialPayloadLoaded ? (
                <div className="response-settings-empty">Loading models…</div>
              ) : models.length > 0 ? (
                models.map((model) => {
                  const selected = model.id === currentModel.id;
                  return (
                    <DropdownMenuItem
                      key={model.id}
                      className={`response-settings-option ${selected ? "selected" : ""}`}
                      onSelect={() => setCurrentModel(model)}
                    >
                      <span className="response-settings-option-label">{model.displayName}</span>
                      {selected && <Check className="response-settings-check" size={14} aria-hidden="true" />}
                    </DropdownMenuItem>
                  );
                })
              ) : (
                <div className="response-settings-empty">No models available.</div>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
